import { getCountryNameFromCode } from "@/utils/geography";
import { getTotalDeclaredValue } from "@/lib/calc-tax-duties";
import {
  getParcelChargeableWeight,
  isIossSchemeLocation,
} from "@/lib/shipment-functions";
import { checkShipmentDimensions } from "@/lib/calc-packing";
import {
  STANDARD_SHIPPING_CONSUMER_SURCHARGE_SGD,
  SHIPPING_METHODS,
  STANDARD_SHIPPING_ENABLED,
  ECOMMERCE_STANDARD_SHIPPING_ENABLED,
  POSTAL_SHIPPING_ENABLED,
} from "@/lib/constants";
import { STANDARD_SHIPPING_PRICES } from "@/lib/shipment/standard-shipping-prices";
import { POSTAL_PRICES } from "@/lib/shipment/postal-shipping-prices";
import { exchangeCurrency } from "@/utils/currency";

const SHIPPING_REQUIREMENTS_FOR_COUNTRIES = {
  STANDARD: {
    AU: {
      totalDeclaredValue: {
        max: 890, // SGD
      },
    },
  },
  POSTAL: {
    AU: {
      totalDeclaredValue: {
        max: 890, // SGD
      },
    },
  },
};

const SHIPPING_REQUIREMENTS_FOR_REGIONS = {
  POSTAL: {
    EU: {
      cif: {
        max: 150,
        currency: "EUR",
      },
    },
  },
};

export const getStandardShippingCost = ({ country, weight, userID }) => {
  const countryPrices = STANDARD_SHIPPING_PRICES[country];
  if (!countryPrices) return null;
  if (typeof weight !== "number" || weight < 0) return null;

  for (const { weight: maxWeight, price } of countryPrices) {
    if (maxWeight >= weight) {
      if (USER_DISCOUNTS[userID]) {
        return getUserDiscountedPrice({
          userID,
          shippingMethod: SHIPPING_METHODS.STANDARD.value,
          maxWeight,
          basePrice: price,
        });
      }
      return price;
    }
  }
  return null;
};

export const getPostalCost = ({ country, weight, userID }) => {
  const countryPrices = POSTAL_PRICES[country];
  if (!countryPrices) return null;
  if (typeof weight !== "number" || weight < 0) return null;

  for (const { weight: maxWeight, price } of countryPrices) {
    if (maxWeight >= weight) {
      if (USER_DISCOUNTS[userID]) {
        return getUserDiscountedPrice({
          userID,
          shippingMethod: SHIPPING_METHODS.POSTAL.value,
          maxWeight,
          basePrice: price,
        });
      }
      return price;
    }
  }
  return null;
};

const isStandardShippingAvailableForShipmentInCountry = ({
  shipment,
  country,
}) => {
  if (!country)
    return {
      isEligible: false,
      shouldDisplayOption: false,
      reason: "Not available",
    };

  const standardShippingPricesForCountry = STANDARD_SHIPPING_PRICES[country];
  if (
    !standardShippingPricesForCountry ||
    standardShippingPricesForCountry.length < 1
  )
    return {
      isEligible: false,
      shouldDisplayOption: false,
      reason: `Not available for ${getCountryNameFromCode(country)}`,
    };

  // If a country doesn't have price for a certain weight, return false
  const maxWeight = Math.max(
    ...standardShippingPricesForCountry.map((priceItem) => priceItem.weight)
  );
  const countryRequirements =
    SHIPPING_REQUIREMENTS_FOR_COUNTRIES.STANDARD[country];
  const { totalDeclaredValue } = countryRequirements || {};
  const { packages } = shipment;

  // The weight is for individual packages, not total shipment
  for (const pkg of packages) {
    const packageChargeableWeight = getParcelChargeableWeight(pkg);
    const packageShippingCost = getStandardShippingCost({
      country,
      weight: packageChargeableWeight,
    });

    if (!packageShippingCost || packageChargeableWeight > maxWeight)
      return {
        isEligible: false,
        shouldDisplayOption: true,
        reason: `Only available for ≤${maxWeight} kg`,
      };
  }

  if (totalDeclaredValue) {
    if (
      packages.some((pkg) => {
        const packageTotalValue = getTotalDeclaredValue([pkg]);
        return (
          totalDeclaredValue.max && packageTotalValue > totalDeclaredValue.max
        );
      })
    )
      return {
        isEligible: false,
        shouldDisplayOption: true,
        reason: `Only available with total declared value ≤$${totalDeclaredValue.max}`,
      };
  }

  return {
    isEligible: true,
    shouldDisplayOption: true,
    reason: "",
  };
};

const isPostalAvailableForShipmentInCountry = ({
  shipment,
  country,
  postalCode,
}) => {
  if (!country)
    return {
      isEligible: false,
      shouldDisplayOption: false,
      reason: "Not available",
    };

  const postalPricesForCountry = POSTAL_PRICES[country];
  if (!postalPricesForCountry || postalPricesForCountry.length < 1)
    return {
      isEligible: false,
      shouldDisplayOption: false,
      reason: `Not available for ${getCountryNameFromCode(country)}`,
    };

  // If a country doesn't have price for a certain weight, return false
  const maxWeight = Math.max(
    ...postalPricesForCountry.map((priceItem) => priceItem.weight)
  );
  const countryRequirements =
    SHIPPING_REQUIREMENTS_FOR_COUNTRIES.POSTAL[country];
  const { totalDeclaredValue } = countryRequirements || {};
  const { packages, userID } = shipment;

  // The weight is for individual packages, not total shipment
  let totalPackageShippingCost = 0;
  for (const pkg of packages) {
    const packageShippingCost = getPostalCost({
      country,
      weight: pkg.weight,
      userID,
    });

    if (!packageShippingCost || pkg.weight > maxWeight) {
      return {
        isEligible: false,
        shouldDisplayOption: true,
        reason: `Only available for ≤${maxWeight} kg`,
      };
    }
    totalPackageShippingCost += packageShippingCost;
  }

  if (isIossSchemeLocation(country, postalCode)) {
    const userTotalDeclaredValue = getTotalDeclaredValue(packages);
    const shipmentCifValue = userTotalDeclaredValue + totalPackageShippingCost;

    const europeShippingRequirements =
      SHIPPING_REQUIREMENTS_FOR_REGIONS.POSTAL["EU"];
    const { cif } = europeShippingRequirements || {};
    const currency = cif?.currency ?? "EUR";
    const cifLimit = exchangeCurrency(cif?.max ?? 0, currency, "SGD");

    if (!!cifLimit && shipmentCifValue > cifLimit) {
      return {
        isEligible: false,
        shouldDisplayOption: true,
        reason: `Only available for CIF value ≤${cif.max} ${cif.currency}`,
      };
    }
  }

  if (totalDeclaredValue) {
    if (
      packages.some((pkg) => {
        const packageTotalValue = getTotalDeclaredValue([pkg]);
        return (
          totalDeclaredValue.max && packageTotalValue > totalDeclaredValue.max
        );
      })
    )
      return {
        isEligible: false,
        shouldDisplayOption: true,
        reason: `Only available with total declared value ≤$${totalDeclaredValue.max}`,
      };
  }

  return {
    isEligible: true,
    shouldDisplayOption: true,
    reason: "",
  };
};

export const isPostalEligibleForShipment = (shipment) => {
  if (!POSTAL_SHIPPING_ENABLED)
    return {
      isEligible: false,
      shouldDisplayOption: false,
      reason: "Postal Shipping is disabled",
    };

  let isEligible;
  let shouldDisplayOption;
  let reason;

  const { packages, recipientCountry, recipientPostalCode } = shipment;
  ({ isEligible, shouldDisplayOption, reason } =
    isPostalAvailableForShipmentInCountry({
      shipment,
      country: recipientCountry,
      postalCode: recipientPostalCode,
    }));
  if (!isEligible)
    return {
      isEligible,
      shouldDisplayOption,
      reason,
    };

  if (packages.some((pkg) => ["Need", "Fragile"].includes(pkg.packingType)))
    return {
      isEligible: false,
      shouldDisplayOption: true,
      reason: "Unavailable with packing service",
    };

  const { isValid: isValidDimensions, reason: dimReason } =
    checkShipmentDimensions({
      ...shipment,
      shippingMethod: SHIPPING_METHODS.POSTAL.value,
    });
  if (!isValidDimensions)
    return {
      isEligible: false,
      shouldDisplayOption: true,
      reason: dimReason,
    };

  return {
    isEligible: true,
    shouldDisplayOption: true,
    reason: "",
  };
};

export const isStandardShippingEligibleForShipment = (shipment) => {
  if (!STANDARD_SHIPPING_ENABLED && !ECOMMERCE_STANDARD_SHIPPING_ENABLED)
    return {
      isEligible: false,
      shouldDisplayOption: false,
      reason: "Standard Shipping is disabled",
    };

  let isEligible;
  let shouldDisplayOption;
  let reason;

  const { packages, recipientCountry } = shipment;
  ({ isEligible, shouldDisplayOption, reason } =
    isStandardShippingAvailableForShipmentInCountry({
      shipment,
      country: recipientCountry,
    }));
  if (!isEligible)
    return {
      isEligible,
      shouldDisplayOption,
      reason,
    };

  if (packages.some((pkg) => ["Need", "Fragile"].includes(pkg.packingType)))
    return {
      isEligible: false,
      shouldDisplayOption: true,
      reason: "Unavailable with packing service",
    };

  const { isValid: isValidDimensions, reason: dimReason } =
    checkShipmentDimensions({
      ...shipment,
      shippingMethod: SHIPPING_METHODS.STANDARD.value,
    });
  if (!isValidDimensions)
    return {
      isEligible: false,
      shouldDisplayOption: true,
      reason: dimReason,
    };

  return {
    isEligible: true,
    shouldDisplayOption: true,
    reason: "",
  };
};

export const isSelectedShippingMethodEligibleForShipment = (shipment) => {
  const { shippingMethod } = shipment;
  if (shippingMethod === SHIPPING_METHODS.STANDARD.value) {
    return isStandardShippingEligibleForShipment(shipment);
  }

  if (shippingMethod === SHIPPING_METHODS.POSTAL.value) {
    return isPostalEligibleForShipment(shipment);
  }

  return {
    isEligible: true,
  };
};

export const allowedShippingMethodsForShipment = (shipment) => {
  const { isEligible: isStandardShippingEligible } =
    isStandardShippingEligibleForShipment(shipment);
  const { isEligible: isPostalEligible } =
    isPostalEligibleForShipment(shipment);

  return [
    SHIPPING_METHODS.EXPRESS,
    ...(isStandardShippingEligible ? [SHIPPING_METHODS.STANDARD] : []),
    ...(POSTAL_SHIPPING_ENABLED && isPostalEligible
      ? [SHIPPING_METHODS.POSTAL]
      : []),
  ];
};

export const getStandardShippingConsumerSurcharge = (isWaived = false) => {
  return isWaived ? 0 : STANDARD_SHIPPING_CONSUMER_SURCHARGE_SGD;
};

export const updateShipmentOnShippingMethodChange = ({
  shippingMethod,
  shipment,
  freightCost = null,
  totalAmount = null,
}) => {
  let newAddOnsFlags = {};

  if (shippingMethod === SHIPPING_METHODS.STANDARD.value) {
    newAddOnsFlags = {
      isInsured: false,
      directSignature: false,
      dutiesPaidBy: "RECIPIENT",
    };
  }

  if (shippingMethod === SHIPPING_METHODS.POSTAL.value) {
    newAddOnsFlags = {
      isInsured: false,
      directSignature: false,
    };
  }

  const newShipment = {
    ...shipment,
    ...newAddOnsFlags,
    shippingMethod,
  };
  if (freightCost) newShipment.initialFreightQuote = freightCost;
  if (totalAmount) newShipment.totalAmount = totalAmount;

  return newShipment;
};

export const adjustShippingMethodIfNotEnabled = (formData) => {
  let newShippingMethod = SHIPPING_METHODS.EXPRESS.value;
  const { recipientCountry, shippingMethod } = formData;

  if (!shippingMethod) return newShippingMethod;

  if (
    shippingMethod === SHIPPING_METHODS.STANDARD.value &&
    (STANDARD_SHIPPING_ENABLED || ECOMMERCE_STANDARD_SHIPPING_ENABLED)
  )
    newShippingMethod = SHIPPING_METHODS.STANDARD.value;

  if (
    shippingMethod === SHIPPING_METHODS.POSTAL.value &&
    POSTAL_SHIPPING_ENABLED
  )
    newShippingMethod = SHIPPING_METHODS.POSTAL.value;

  return newShippingMethod;
};

export const fallbackShippingMethodIfNotSupported = (formData) => {
  let newShippingMethod = SHIPPING_METHODS.EXPRESS.value;
  const { shippingMethod } = formData;

  if (!shippingMethod) return newShippingMethod;

  const fallbackHierarchy = {
    [SHIPPING_METHODS.STANDARD.value]: [
      SHIPPING_METHODS.STANDARD.value,
      SHIPPING_METHODS.POSTAL.value,
      SHIPPING_METHODS.EXPRESS.value,
    ],
    [SHIPPING_METHODS.POSTAL.value]: [
      SHIPPING_METHODS.POSTAL.value,
      SHIPPING_METHODS.STANDARD.value,
      SHIPPING_METHODS.EXPRESS.value,
    ],
  };

  // Return the same shipping method if it's not in the fallback hierarchy
  const fallbackShippingMethods = fallbackHierarchy[shippingMethod];
  if (!fallbackShippingMethods) return shippingMethod;

  for (const fallbackShippingMethod of fallbackShippingMethods) {
    if (
      fallbackShippingMethod === SHIPPING_METHODS.STANDARD.value &&
      isStandardShippingEligibleForShipment(formData)?.isEligible
    ) {
      newShippingMethod = SHIPPING_METHODS.STANDARD.value;
      break;
    }

    if (
      fallbackShippingMethod === SHIPPING_METHODS.POSTAL.value &&
      isPostalEligibleForShipment(formData)?.isEligible
    ) {
      newShippingMethod = SHIPPING_METHODS.POSTAL.value;
      break;
    }

    if (fallbackShippingMethod === SHIPPING_METHODS.EXPRESS.value) {
      newShippingMethod = SHIPPING_METHODS.EXPRESS.value;
      break;
    }
  }

  return newShippingMethod;
};

const USER_DISCOUNTS = {
  "google-110069479496051210252": {
    // Mirabelle
    rules: [
      {
        shippingMethods: [SHIPPING_METHODS.STANDARD.value],
        condition: ({ maxWeight }) => maxWeight === 0.25,
        discountedPrice: 14,
      },
      // Add more rules here if needed
    ],
  },
  // Add more users here if needed
};

const getUserDiscountedPrice = ({
  userID,
  shippingMethod,
  maxWeight,
  basePrice,
}) => {
  const userDiscountInfo = USER_DISCOUNTS[userID];
  if (!userDiscountInfo) return basePrice;

  for (const rule of userDiscountInfo.rules) {
    if (rule.shippingMethods && !rule.shippingMethods.includes(shippingMethod))
      continue;

    if (rule.condition && rule.condition({ maxWeight })) {
      return rule.discountedPrice;
    }
  }

  return basePrice;
};
